import * as React from 'react';

import {
    ConfigViewMetric,
    ConfigViewPillar,
    flatten,
} from './config.insight.model';
import {
    CustomizeMetricsInsightProps,
    CustomizeMetricsInsightState,
    NestedMetric,
} from './customize-metrics.insight.model';
import { MetricPath } from './insight.state';
import { NestedNodeSelectInsight } from './nested-node-select.insight.component';
import { NestedNode } from './nested-node-select.insight.model';

function mapToNestedMetric(
    pillar: ConfigViewPillar,
    metric: ConfigViewMetric,
    lookup: MetricPath[],
    parentPair: MetricPath,
    depth: number,
): NestedMetric {
    const pair = lookup
        .filter((e) => e[2].length === depth + 1)
        .find(
            (e) =>
                e[0] === pillar &&
                e[1].metricId === metric.metricId &&
                (!parentPair ||
                    e[2][depth - 1].metricId === parentPair[1].metricId),
        );
    if (!pair) return null;
    return {
        pair,
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        components:
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            metric.components &&
            metric.components
                .map((e) =>
                    mapToNestedMetric(pillar, e, lookup, pair, depth + 1),
                )
                .filter((e) => !!e),
    };
}

function pairToId(pair: MetricPath): string {
    return `${pair[0].identifier}-${pair[2].reduce(
        (p, c) => `${p}-${c.metricId}`,
        '',
    )}`;
}

function mapToNode(nm: NestedMetric): NestedNode {
    return {
        id: pairToId(nm.pair),
        label: nm.pair[1].shortName,
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        children: nm.components && nm.components.map(mapToNode),
    };
}

function mapSelectionToNode(pair: MetricPath): NestedNode {
    return {
        id: pairToId(pair),
        label: null,
    };
}

function mapToMetric(node: NestedNode, lookup: MetricPath[]): MetricPath {
    return lookup.find((e) => pairToId(e) === node.id);
}

class CustomizeMetricsInsightImpl extends React.Component<
    CustomizeMetricsInsightProps,
    CustomizeMetricsInsightState
> {
    constructor(props: CustomizeMetricsInsightProps) {
        super(props);

        this._propsToState();
    }

    private _propsToState(): void {
        const metricsFlatten = flatten<ConfigViewPillar, MetricPath>(
            this.props.pillars,
            (i) =>
                flatten<
                    | ConfigViewMetric
                    | [ConfigViewMetric, ConfigViewMetric]
                    | any,
                    MetricPath
                >(
                    i.metrics,
                    (ii) => {
                        if (Array.isArray(ii)) {
                            const m = this.props.metrics.find(
                                (ee) => ee.metricId === ii[1].metricId,
                            );
                            return [[i, m, [ii[0], ii[1]]] as MetricPath];
                        } else {
                            const m = this.props.metrics.find(
                                (ee) => ee.metricId === ii.metricId,
                            );
                            return [[i, m, [ii]] as MetricPath];
                        }
                    },
                    (ii) =>
                        !Array.isArray(ii) && ii.components
                            ? ii.components.map((e) => [ii, e])
                            : null,
                ),
            (i) => i.pillars,
        ).filter((e) => e[1]);

        const nestedMetrics = flatten(
            this.props.pillars,
            (i) =>
                i.metrics
                    .map((e) =>
                        mapToNestedMetric(i, e, metricsFlatten, null, 0),
                    )
                    .filter((e) => !!e),
            (i) => i.pillars,
        );

        const nodes = nestedMetrics.map(mapToNode);
        const selection = this.props.selection.map(mapSelectionToNode);
        this.state = { metricsFlatten, nodes, selection };
    }

    render(): React.ReactNode {
        return (
            <NestedNodeSelectInsight
                nodes={this.state.nodes}
                selection={this.state.selection}
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                onChange={(e) =>
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.props.onChange &&
                    this.props.onChange({
                        selected: e.selected.map((ee) =>
                            mapToMetric(ee, this.state.metricsFlatten),
                        ),
                    })
                }
                canSelect={(node, checked) =>
                    !this.props.canSelect
                        ? true
                        : this.props.canSelect(
                              mapToMetric(node, this.state.metricsFlatten),
                              checked,
                          )
                }
                standAloneParent={true}
            />
        );
    }
}

export const CustomizeMetricsInsight = CustomizeMetricsInsightImpl;
